import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER } from "@icoach/router/routerPath";
import { getAddMonths, isAfter, parseDate, getDate } from "@util/moment";
import { isFunction } from "@util/utils";
import { EFFECT_CONTENT_TYPE } from "@constants/";

/**
 * 根據 effectType 陣列決定對應的 URL
 * @param {Array<number>} effectType - effectType 陣列
 * @param {number} cycleOrderID - 循環訂單 ID
 * @param {number} effectID - 變更單 ID
 * @returns {string} 對應的路由 URL
 */
const getEffectOrderEditUrl = (effectType, cycleOrderID, effectID) => {
	if (!Array.isArray(effectType) || effectType.length === 0) {
		// 預設使用 CHANGE 路由
		return DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.CHANGE.GET_PATH(cycleOrderID, effectID);
	}

	// 檢查優先級：STOP > QUIT > RESTOP > 其他（使用 CHANGE）
	if (effectType.includes(EFFECT_CONTENT_TYPE.STOP)) {
		return DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.SUSPEND.GET_PATH(cycleOrderID, effectID);
	}

	if (effectType.includes(EFFECT_CONTENT_TYPE.QUIT)) {
		return DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.TERMINATE.GET_PATH(cycleOrderID, effectID);
	}

	if (effectType.includes(EFFECT_CONTENT_TYPE.RESTOP)) {
		return DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.RESUME.GET_PATH(cycleOrderID, effectID);
	}

	// ITEMS, SHIPPINGDAY, RECEIVER, PAYMENT, INVOICE 都使用 CHANGE 路由
	return DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.CHANGE.GET_PATH(cycleOrderID, effectID);
};

/**
 * 取得變更單操作選單，根據變更單參數和權限生成操作選單
 *
 * @param {Object} params - 參數物件
 * @param {boolean} params.canDeleted - 是否可刪除
 * @param {Array<number>} params.effectType - 變更單類型陣列
 * @param {string|number} params.memberID - 會員 ID
 * @param {string|number} params.cycleOrderID - 循環購買訂單 ID
 * @param {string|number} params.effectID - 變更單 ID
 * @param {string} params.url - PDF 連結
 * @param {Object} extend - 擴展參數
 * @param {Object} extend.dialogRef - 彈窗引用
 * @param {Function} extend.refresh - 刷新回調
 * @param {Function} extend.showPDF - 顯示 PDF 回調
 * @returns {Array<Object>} 操作選單陣列
 */
export const getEffectOrderMenuMap = ({ canDeleted, canModify, effectType, memberID, cycleOrderID, effectID, url: pdfUrl }, extend) => {
	const { dialogRef, refresh, showPDF } = extend || {};
	const { openCyclePurchaseEffectDialog, openCycleEffectOrderDeleteDialog } = dialogRef.current || {};

	return [
		{
			text: "查看",
			condition: true,
			onClick: () =>
				isFunction(openCyclePurchaseEffectDialog) &&
				openCyclePurchaseEffectDialog({
					cycleOrderID,
					effectID,
				}),
		},
		{
			text: "編輯",
			condition: canModify,
			component: Link,
			to: getEffectOrderEditUrl(effectType, cycleOrderID, effectID),
		},
		{
			text: "刪除",
			condition: canDeleted,
			onClick: () => isFunction(openCycleEffectOrderDeleteDialog) && openCycleEffectOrderDeleteDialog({ memberID, cycleOrderID, effectID }, refresh),
		},
		{
			text: "查看PDF",
			condition: Boolean(pdfUrl),
			onClick: () => showPDF({ open: true, pdfUrl: pdfUrl }),
		},
	];
};

/**
 * 取得健康循環購買訂單操作選單，根據訂單參數和權限生成操作選單
 *
 * @param {Object} params - 參數物件
 * @param {string|number} params.memberID - 會員 ID
 * @param {string|number} params.cycleOrderID - 循環購買訂單 ID
 * @param {string|number} params.effectID - 變更單 ID
 * @param {boolean} params.canReview - 是否可查看
 * @param {boolean} params.canModify - 是否可變更
 * @param {boolean} params.canSuspend - 是否可暫停
 * @param {boolean} params.canTerminate - 是否可終止
 * @param {boolean} params.canResume - 是否可恢復暫停
 * @param {boolean} params.canDeleted - 是否可刪除
 * @param {boolean} params.canChangeEmployee - 是否可變更建單擔當
 * @param {Object} extend - 擴展參數
 * @param {Object} extend.dialogRef - 彈窗引用
 * @param {Function} extend.refresh - 刷新回調
 * @returns {Array<Object>} 操作選單陣列
 */
export const getHealthyCyclePurchaseMenuMap = (
	{ memberID, cycleOrderID, effectID, canReview, canModify, canSuspend, canCancel: canTerminate, canResumeNextCycle: canResume, canDeleted, canEnableFirstOrder, canChangeEmployee } = {},
	extend,
) => {
	const { dialogRef, refresh } = extend || {};
	const { openCyclePurchaseDeleteDialog, openHealthyCycleFirstOrderDialog, openCyclePurchaseCoachChangeDialog } = dialogRef.current || {};

	return [
		{
			text: "查看",
			condition: canReview,
			component: Link,
			to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_MAIN_PATH(memberID, cycleOrderID),
		},
		{
			text: "現場首購",
			condition: canEnableFirstOrder,
			onClick: () => isFunction(openHealthyCycleFirstOrderDialog) && openHealthyCycleFirstOrderDialog({ cycleOrderID }, refresh),
		},
		{
			text: "訂單變更",
			condition: canModify,
			component: Link,
			to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.CHANGE.GET_PATH(cycleOrderID, effectID),
		},
		{
			text: "訂單暫停",
			condition: canSuspend,
			component: Link,
			to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.SUSPEND.GET_PATH(cycleOrderID, effectID),
		},
		{
			text: "訂單終止",
			condition: canTerminate,
			component: Link,
			to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.TERMINATE.GET_PATH(cycleOrderID, effectID),
		},
		{
			text: "恢復暫停",
			condition: canResume,
			component: Link,
			to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.RESUME.GET_PATH(cycleOrderID, effectID),
		},
		{
			text: "擔當變更",
			condition: canChangeEmployee,
			onClick: () => isFunction(openCyclePurchaseCoachChangeDialog) && openCyclePurchaseCoachChangeDialog({ memberID, cycleOrderID }, refresh),
		},
		{
			text: "刪除",
			condition: canDeleted,
			onClick: () => isFunction(openCyclePurchaseDeleteDialog) && openCyclePurchaseDeleteDialog({ cycleOrderID }, refresh),
		},
	];
};

/**
 * 過濾選單項目
 *
 * 功能：
 * - 根據條件過濾選單項目
 * - 支援函數和布林值條件
 * - 移除條件屬性後返回
 *
 * @param {Array<Object>} menuList - 選單項目陣列
 * @returns {Array<Object>} 過濾後的選單項目陣列
 */
export const filterMenu = menuList => {
	return menuList
		.filter(({ condition }) => {
			if (isFunction(condition)) return condition() || false;
			return condition;
		})
		.map(item => {
			delete item.condition;
			return item;
		});
};

// 取得初始分頁參數
export const getInitialPagination = sortBy => {
	let result = { pageIndex: 1, pageSize: 50 };
	if (sortBy) {
		result = { ...result, sortBy };
	}
	return result;
};

export const COLOR_MATCH = {
	1: "success",
	2: "info",
	3: "grey",
	4: "warning",
	9: "error",
};

/**
 * 判斷是否超過出貨截止日
 * @param {Date} date 指定日期
 * @param {number} shippingDay 出貨日
 */
export const isAfterDeadline = (date, shippingDay) => {
	let deadline = shippingDay - 5;
	deadline = parseDate(dayjs(date).date(deadline));
	return isAfter(date, deadline);
};

/**
 * 取得生效日 日期
 * @param {Date} date 指定日期
 * @param {number} shippingDay 出貨日
 */
export const getEffectiveDate = (date, shippingDay) => {
	let _date = date;
	if (isAfterDeadline(_date, shippingDay)) {
		_date = getAddMonths(_date, 1);
	}
	return _date;
};

// 漲價通知（2026/11 漲價）顯示期間：2026/10/01 ~ 2026/11/02 09:00
// 過期後整個函式與呼叫端的 showPriceNotice 一併刪除
const PRICE_NOTICE_START = "2026-10-01";
const PRICE_NOTICE_END = "2026-11-02 09:00:00";

/**
 * 是否在漲價通知顯示期間內
 * @returns {boolean}
 */
export const isPriceNoticePeriod = () => {
	const today = getDate();
	if (today < PRICE_NOTICE_START) return false;
	return dayjs().isBefore(dayjs(PRICE_NOTICE_END));
};
